import { useState, useEffect } from "react";
import { ShoppingBag, Check, Crown, Star, Palmtree, Sparkles, Eye, X, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api } from "@/App";
import { useLang } from "@/i18n";
import { IslandScene } from "./IslandScene";

const TIER_CONFIG = {
  base: { color: "from-emerald-400 to-teal-500", badge: "Base", icon: Palmtree },
  premium: { color: "from-amber-400 to-orange-500", badge: "Premium", icon: Star },
  ultra: { color: "from-purple-500 to-pink-500", badge: "Ultra", icon: Crown },
};

// Celebration particles
function CelebrationOverlay({ islandImage, islandName, onDone }) {
  const [particles] = useState(() =>
    Array.from({ length: 40 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      delay: Math.random() * 0.8,
      size: 6 + Math.random() * 10,
      duration: 1.5 + Math.random() * 1.5,
      type: ['star', 'coin', 'spark'][Math.floor(Math.random() * 3)],
    }))
  );

  useEffect(() => {
    const timer = setTimeout(onDone, 4000);
    return () => clearTimeout(timer);
  }, [onDone]);

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center" onClick={onDone}>
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-md animate-in fade-in duration-300" />

      {/* Particles */}
      {particles.map((p) => (
        <div
          key={p.id}
          className="absolute pointer-events-none"
          style={{
            left: `${p.x}%`,
            bottom: '-10px',
            width: `${p.size}px`,
            height: `${p.size}px`,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
            animation: `celebrationRise ${p.duration}s ease-out ${p.delay}s forwards`,
            opacity: 0,
          }}
        >
          {p.type === 'star' ? (
            <Star className="w-full h-full text-amber-300 fill-amber-300" />
          ) : p.type === 'coin' ? (
            <span className="block w-full h-full rounded-full bg-gradient-to-br from-amber-200 to-yellow-500 shadow-[0_0_10px_rgba(255,193,7,0.8)]" />
          ) : (
            <Sparkles className="w-full h-full text-yellow-200" />
          )}
        </div>
      ))}

      {/* Center content */}
      <div className="relative z-10 flex flex-col items-center animate-in zoom-in-50 duration-500">
        {/* Glow ring */}
        <div className="relative mb-6">
          <div className="absolute inset-0 rounded-full bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-400 blur-2xl opacity-60 animate-pulse scale-125" />
          <div className="relative w-48 h-48 rounded-2xl overflow-hidden border-4 border-amber-400 shadow-2xl shadow-amber-400/50">
            <img src={islandImage} alt="" className="w-full h-full object-cover" />
          </div>
          <div className="absolute -top-3 -right-3 w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center shadow-lg animate-bounce">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
        </div>

        {/* Text */}
        <h2 className="text-2xl font-bold text-white mb-2 text-center">
          Nuova Isola Sbloccata!
        </h2>
        <p className="text-amber-300 text-lg font-semibold mb-4">{islandName}</p>
        <p className="text-white/50 text-sm">Tocca per continuare</p>
      </div>

      {/* CSS Keyframes */}
      <style>{`
        @keyframes celebrationRise {
          0% { transform: translateY(0) scale(0); opacity: 0; }
          15% { opacity: 1; transform: translateY(-10vh) scale(1); }
          100% { transform: translateY(-100vh) scale(0.5) rotate(${Math.random() > 0.5 ? '' : '-'}360deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

export function IslandShop({ onClose, onPurchase }) {
  const { lang, t } = useLang();
  const [catalog, setCatalog] = useState([]);
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [celebration, setCelebration] = useState(null);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    fetchCatalog();
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const fetchCatalog = async () => {
    try {
      const res = await api.get("/islands/shop/catalog");
      setCatalog(res.data.catalog);
      setBalance(res.data.balance);
    } catch (e) {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (islandId) => {
    setPurchasing(islandId);
    try {
      const res = await api.post("/islands/shop/purchase", { island_id: islandId });
      setBalance(res.data.new_balance);
      // Show celebration
      const island = catalog.find(i => i.id === islandId);
      setCelebration({
        image: island?.image,
        name: lang === 'en' ? island?.name_en : island?.name,
      });
      fetchCatalog();
      onPurchase?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nell'acquisto");
    } finally {
      setPurchasing(null);
    }
  };

  const handleEquip = async (islandId) => {
    try {
      const res = await api.post("/islands/shop/equip", { island_id: islandId });
      toast.success(res.data.message);
      fetchCatalog();
      onPurchase?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-[9500] bg-black/60 backdrop-blur-sm flex items-center justify-center">
        <div className="w-8 h-8 border-3 border-white/30 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <>
      {/* Celebration overlay */}
      {celebration && (
        <CelebrationOverlay
          islandImage={celebration.image}
          islandName={celebration.name}
          onDone={() => setCelebration(null)}
        />
      )}

      {/* Fullscreen live preview with weather & sounds */}
      {preview && (
        <div className="fixed inset-0 z-[9700]" data-testid="island-preview">
          <IslandScene islandType={preview.id} />
          <button
            onClick={() => setPreview(null)}
            className="absolute top-4 left-4 z-20 w-10 h-10 rounded-full flex items-center justify-center active:scale-90 transition-transform"
            style={{ background: "rgba(8,12,24,0.55)", backdropFilter: "blur(12px)", border: "1px solid rgba(255,255,255,0.15)" }}
            data-testid="close-preview-btn"
          >
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          {/* Bottom glass card */}
          <div className="absolute bottom-0 inset-x-0 z-20 p-4 pb-8">
            <div className="rounded-3xl p-5"
                 style={{ background: "rgba(8,12,24,0.6)", backdropFilter: "blur(20px)", border: "1px solid rgba(255,255,255,0.14)" }}>
              <h3 className="text-white font-extrabold text-xl">{lang === 'en' ? preview.name_en : preview.name}</h3>
              <p className="text-white/60 text-xs mt-1 mb-4">{lang === 'en' ? preview.description_en : preview.description}</p>
              <div className="flex items-center justify-between gap-3">
                {preview.price === 0 ? (
                  <span className="text-sm font-semibold text-emerald-300">{lang === 'en' ? 'Free' : 'Gratuita'}</span>
                ) : (
                  <div className="flex items-center gap-1.5">
                    <img src="/images/ade-coin.jpeg" alt="" className="w-5 h-5 rounded-full object-cover" />
                    <span className="text-lg font-bold text-amber-300">{preview.price.toLocaleString()}</span>
                  </div>
                )}
                {preview.equipped ? (
                  <span className="text-xs text-cyan-300 font-semibold px-4 py-2 rounded-full" style={{ background: "rgba(34,211,238,0.12)", border: "1px solid rgba(34,211,238,0.3)" }}>
                    {lang === 'en' ? 'Currently active' : 'Attualmente attiva'}
                  </span>
                ) : preview.owned || preview.price === 0 ? (
                  <Button onClick={() => { handleEquip(preview.id); setPreview(null); }}
                    className="rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-6 shadow-lg"
                    data-testid="preview-equip-btn">
                    {lang === 'en' ? 'Equip' : 'Attiva'}
                  </Button>
                ) : (
                  <Button onClick={() => { setPreview(null); handlePurchase(preview.id); }}
                    disabled={!preview.can_afford}
                    className={`rounded-full px-6 shadow-lg ${preview.can_afford ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white' : 'bg-white/10 text-white/40'}`}
                    data-testid="preview-buy-btn">
                    {preview.can_afford ? (lang === 'en' ? 'Buy' : 'Acquista') : (lang === 'en' ? 'Not enough coins' : 'Coins insufficienti')}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="fixed inset-0 z-[9500] bg-black/60 backdrop-blur-sm flex flex-col" data-testid="island-shop">
        {/* Header */}
        <div className="bg-gradient-to-r from-cyan-600 via-blue-600 to-purple-600 pt-12 pb-6 px-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <ShoppingBag className="w-6 h-6 text-white" />
              <h1 className="text-2xl font-bold text-white">Island Shop</h1>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white text-lg"
              data-testid="close-shop-btn"
            >
              &times;
            </button>
          </div>
          {/* Balance */}
          <div className="flex items-center gap-2 bg-white/10 rounded-xl px-4 py-2.5 backdrop-blur-sm">
            <img src="/images/ade-coin.jpeg" alt="" className="w-5 h-5 rounded-full object-cover bg-white dark:bg-black" />
            <span className="text-white/70 text-sm">{lang === 'en' ? 'Your balance' : 'Il tuo saldo'}:</span>
            <span className="text-white font-bold text-lg ml-auto">{Math.floor(balance).toLocaleString()}</span>
          </div>
        </div>

        {/* Catalog */}
        <div className="flex-1 overflow-y-auto bg-gray-50 px-4 py-5 space-y-4 pb-20">
          {catalog.map((island) => {
            const tier = TIER_CONFIG[island.tier] || TIER_CONFIG.base;
            const TierIcon = tier.icon;
            const isEquipped = island.equipped;
            const isOwned = island.owned;
            const canAfford = island.can_afford;
            const isFree = island.price === 0;

            return (
              <div
                key={island.id}
                className={`rounded-2xl overflow-hidden shadow-lg border-2 transition-all ${
                  isEquipped ? "border-cyan-400 shadow-cyan-100" : "border-white"
                }`}
                data-testid={`island-card-${island.id}`}
              >
                {/* Island Image — tap for live preview */}
                <div className="relative cursor-pointer" onClick={() => setPreview(island)} data-testid={`preview-open-${island.id}`}>
                  <img src={island.image} alt={island.name} className="w-full h-44 object-cover" />
                  {/* Preview hint */}
                  <div className={`absolute ${isOwned || isEquipped ? 'top-12' : 'top-3'} right-3 flex items-center gap-1.5 px-3 py-1 rounded-full`}
                       style={{ background: "rgba(8,12,24,0.55)", backdropFilter: "blur(8px)", border: "1px solid rgba(255,255,255,0.2)" }}>
                    <Eye className="w-3.5 h-3.5 text-white" />
                    <span className="text-white text-[10px] font-bold">{lang === 'en' ? 'Preview' : 'Anteprima'}</span>
                  </div>
                  {/* Tier badge */}
                  <div className={`absolute top-3 left-3 px-3 py-1 rounded-full bg-gradient-to-r ${tier.color} flex items-center gap-1.5 shadow-lg`}>
                    <TierIcon className="w-3.5 h-3.5 text-white" />
                    <span className="text-white text-xs font-bold">{tier.badge}</span>
                  </div>
                  {/* Equipped badge */}
                  {isEquipped && (
                    <div className="absolute top-3 right-3 px-3 py-1 rounded-full bg-cyan-500 flex items-center gap-1 shadow-lg">
                      <Check className="w-3.5 h-3.5 text-white" />
                      <span className="text-white text-xs font-bold">
                        {lang === 'en' ? 'Equipped' : 'Attiva'}
                      </span>
                    </div>
                  )}
                  {/* Owned badge */}
                  {isOwned && !isEquipped && (
                    <div className="absolute top-3 right-3 px-3 py-1 rounded-full bg-green-500/90 flex items-center gap-1 shadow-lg">
                      <Check className="w-3.5 h-3.5 text-white" />
                      <span className="text-white text-xs font-bold">
                        {lang === 'en' ? 'Owned' : 'Posseduta'}
                      </span>
                    </div>
                  )}
                  {/* Dark gradient */}
                  <div className="absolute bottom-0 inset-x-0 h-20 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-3 left-3 right-3">
                    <h3 className="text-white font-bold text-lg drop-shadow-lg">
                      {lang === 'en' ? island.name_en : island.name}
                    </h3>
                    <p className="text-white/80 text-xs drop-shadow">
                      {lang === 'en' ? island.description_en : island.description}
                    </p>
                  </div>
                </div>

                {/* Actions */}
                <div className="bg-white p-4 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {isFree ? (
                      <span className="text-sm font-semibold text-green-600">
                        {lang === 'en' ? 'Free' : 'Gratuita'}
                      </span>
                    ) : (
                      <div className="flex items-center gap-1.5">
                        <img src="/images/ade-coin.jpeg" alt="" className="w-4 h-4 rounded-full object-cover bg-white dark:bg-black" />
                        <span className={`text-sm font-bold ${canAfford || isOwned ? 'text-amber-600' : 'text-red-400'}`}>
                          {island.price.toLocaleString()}
                        </span>
                      </div>
                    )}
                  </div>

                  {isEquipped ? (
                    <span className="text-xs text-cyan-600 font-semibold px-3 py-1.5 bg-cyan-50 rounded-full">
                      {lang === 'en' ? 'Currently active' : 'Attualmente attiva'}
                    </span>
                  ) : isOwned ? (
                    <Button
                      onClick={() => handleEquip(island.id)}
                      size="sm"
                      className="rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-5 shadow-lg"
                      data-testid={`equip-${island.id}`}
                    >
                      {lang === 'en' ? 'Equip' : 'Attiva'}
                    </Button>
                  ) : isFree ? (
                    <span className="text-xs text-gray-400">{lang === 'en' ? 'Default' : 'Predefinita'}</span>
                  ) : (
                    <Button
                      onClick={() => handlePurchase(island.id)}
                      disabled={!canAfford || purchasing === island.id}
                      size="sm"
                      className={`rounded-full px-5 shadow-lg ${
                        canAfford
                          ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white'
                          : 'bg-gray-200 text-gray-400'
                      }`}
                      data-testid={`buy-${island.id}`}
                    >
                      {purchasing === island.id ? (
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : canAfford ? (
                        lang === 'en' ? 'Buy' : 'Acquista'
                      ) : (
                        lang === 'en' ? 'Not enough coins' : 'Coins insufficienti'
                      )}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
